import React from 'react';
import cn from 'classnames';
import Link from '../Link/Link';
import Panel from '../MarginSpace/Panel/Panel';
import Title from '../Typography/Title/Title';
import SmallText from '../Typography/SmallText/SmallText';
import styles from './ProductCard.module.scss';
import CardPrice from './CardPrice/CardPrice';
import List from '../List/List';
import { CommonProps } from '../Element/Element';
import { CardPriceProps } from './CardPrice/CardPrice';
import CardInfo, { CardInfoProps } from './CardInfo/CardInfo';
import CardTag from './CardTag/CardTag';
import { SupportedTagColor } from '../Tag/Tag';
import Div from '../Element/Div/Div';

type ProductCardProps = {
  /** Card Title */
  titleText: string;
  /** Product description*/
  descriptionText?: string;
  /** List contents */
  listContent?: string[];
  /** Price content to be appended to end of card */
  priceContent?: CardPriceProps;
  /** Text on the tag*/
  tagText?: string;
  /**Tag text color*/
  tagColor?: SupportedTagColor;
  /** URL of the product image */
  imageSrc?: string;
  /** Override default image with this component */
  imageComponent?: React.ReactNode;
  /** Element type of the wrapper; can be used to change the default Link wrapper to something else */
  wrapper?: any;
} & CommonProps<'div'> &
  CardInfoProps;

const ProductCard: React.FunctionComponent<ProductCardProps> & {
  Price: React.FunctionComponent<CardPriceProps>;
} = ({
  titleText,
  productNumber,
  descriptionText,
  listContent,
  priceContent,
  imageSrc,
  imageComponent,
  tagText,
  tagColor,
  className,
  wrapper: Wrapper = Link,
  rating,
  colorVariants,
  ...props
}) => {
  return (
    <Wrapper className={cn([styles.productCard, className])} {...props}>
      <Panel
        className={styles.productCard_panel}
        hoverBoxShadow="md"
        occupyHorizontalSpace
        tagName="article"
      >
        <CardTag
          color={tagColor}
          className={styles.productCard_tag}
          hidden={!tagText}
        >
          {tagText ?? ''}
        </CardTag>

        {imageComponent ? (
          <Div className={styles.productCard_imageContainer}>
            {imageComponent}
          </Div>
        ) : (
          <img
            loading="lazy"
            src={imageSrc}
            alt={titleText}
            className={styles.productCard_image}
          />
        )}

        <Title
          block
          color="black"
          hideOverflow
          tagName="h1"
          className={styles.productCard_title}
        >
          {titleText}
        </Title>

        {(rating || productNumber || colorVariants) && (
          <CardInfo
            className={styles.productCard_rating}
            productNumber={productNumber}
            rating={rating}
            colorVariants={colorVariants}
          />
        )}

        {listContent ? (
          <List className={styles.productCard_listContent} disc>
            {listContent.map((listItem) => (
              <List.Item
                key={listItem.replace(/[^\w\s]/gi, '').replace(/\s/gi, '-')}
              >
                {listItem}
              </List.Item>
            ))}
          </List>
        ) : (
          <SmallText
            className={styles.productCard_description}
            tagName="p"
            block
            color="black"
          >
            {descriptionText}
          </SmallText>
        )}

        {priceContent && <ProductCard.Price {...priceContent} />}
      </Panel>
    </Wrapper>
  );
};

ProductCard.Price = CardPrice;

export default ProductCard;
